import React from 'react';
import { Link } from 'react-router-dom';

// Privacy policy reflecting Dynalink's actual data practices. Keep this in
// sync with what the backend stores — see SECURITY.md in the repo.
const PrivacyPolicy = () => (
  <div className="max-w-3xl mx-auto prose-sm">
    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900 mb-2">Privacy Policy</h1>
    <p className="text-sm text-ink-500 mb-8">Last updated: 11 June 2026</p>

    <div className="space-y-8 text-sm text-ink-700 leading-relaxed">
      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">1. Who we are</h2>
        <p>
          Dynalink ("we", "us") provides link shortening, link-in-bio profile pages, and click
          analytics. For privacy questions or to exercise your rights, contact us at{' '}
          <a className="text-brand-600 underline" href="mailto:dhanasekar.rohith@gmail.com">dhanasekar.rohith@gmail.com</a>.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">2. Data we collect</h2>
        <p className="mb-2"><strong>Account data</strong> — username, email address, and a salted hash of your password (we never store the password itself). If you enable two-factor authentication we store your TOTP secret.</p>
        <p className="mb-2"><strong>Content you create</strong> — short links, destination URLs, profile pages, and any lead-capture submissions your visitors send you.</p>
        <p className="mb-2"><strong>Click and view analytics</strong> — when someone opens a short link or profile page we record the time, browser and OS family, device type, referrer, and an approximate location (country/region/city) derived from the visitor's IP address. <strong>The IP address itself is never stored</strong>: it is used in memory for the location lookup and then replaced with an irreversible keyed hash so we can count unique visitors without identifying anyone.</p>
        <p><strong>Security records</strong> — sessions (device identifier, browser, hashed IP) and an audit trail of administrative actions.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">3. Why we process it (legal bases)</h2>
        <p>We process account and content data to provide the service you signed up for (contract). Analytics with pseudonymised IPs, fraud prevention, and security logging are processed under our legitimate interest in running a reliable, abuse-free service. We send transactional email (verification, password reset) as part of the contract; we do not send marketing email.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">4. Cookies</h2>
        <p>We use a single strictly-necessary cookie: <code>refresh_token</code>, which keeps you signed in. It is HttpOnly, restricted to authentication endpoints, and never used for tracking or advertising. We set no analytics or third-party cookies, so no cookie consent banner choice is required — we simply tell you it exists. See also our <Link className="text-brand-600 underline" to="/terms">Terms of Service</Link>.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">5. Retention</h2>
        <p>Click and page-view analytics are deleted automatically after 365 days. Security audit logs are kept for 730 days. Expired sign-in sessions and one-time email tokens are purged within days of expiring. Account and content data are kept until you delete them or your account.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">6. Your rights</h2>
        <p className="mb-2">Under the GDPR and similar laws you can access, correct, export, and erase your data:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Export</strong> — use "Download my data" on your <Link className="text-brand-600 underline" to="/profile">profile page</Link> to receive a complete machine-readable copy.</li>
          <li><strong>Erasure</strong> — use "Delete account" on the same page. This permanently removes your account, links, profile pages, and analytics.</li>
          <li><strong>Correction</strong> — edit your username and email on your profile page at any time.</li>
          <li>You may also lodge a complaint with your local data-protection authority.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">7. Sharing and transfers</h2>
        <p>We do not sell personal data. Data is shared only with the infrastructure providers that host the service (server and database hosting, transactional email delivery), bound by data-processing agreements. Visitors' approximate locations are derived from a local geolocation database — no IP is sent to third parties for this purpose.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">8. Security</h2>
        <p>Passwords are hashed with bcrypt, sign-in uses short-lived tokens with rotating refresh tokens, API keys are stored as hashes, and all traffic is encrypted in transit with TLS. Access to production systems is restricted and audited.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-ink-900 mb-2">9. Changes</h2>
        <p>If this policy changes materially we will notify registered users by email or an in-app notice before the change takes effect.</p>
      </section>
    </div>
  </div>
);

export default PrivacyPolicy;
